/**
 * Currency formatting utilities
 * Provides consistent currency formatting for Brazilian Real (BRL)
 */

/**
 * Formats a number to Brazilian currency format (R$ 1.234,56)
 * @param value - Numeric value to format
 * @returns Formatted currency string or '-' if invalid
 * @example
 * formatCurrency(1234.56) // Returns 'R$ 1.234,56'
 * formatCurrency(null) // Returns '-'
 * formatCurrency(0) // Returns 'R$ 0,00'
 */
export const formatCurrency = (value?: number | null): string => {
  if (value === null || value === undefined) return '-';

  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  } catch {
    return '-';
  }
};

/**
 * Formats a number to Brazilian currency format without the currency symbol
 * @param value - Numeric value to format
 * @returns Formatted number string or '-' if invalid
 * @example
 * formatCurrencyWithoutSymbol(1234.56) // Returns '1.234,56'
 * formatCurrencyWithoutSymbol(null) // Returns '-'
 */
export const formatCurrencyWithoutSymbol = (value?: number | null): string => {
  if (value === null || value === undefined) return '-';

  try {
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return '-';
  }
};

/**
 * Formats a number to compact Brazilian currency format (e.g., R$ 1,2 mil, R$ 1,5 mi)
 * @param value - Numeric value to format
 * @returns Compact formatted currency string or '-' if invalid
 * @example
 * formatCurrencyCompact(1234) // Returns 'R$ 1,23 mil'
 * formatCurrencyCompact(1234567) // Returns 'R$ 1,23 mi'
 * formatCurrencyCompact(null) // Returns '-'
 */
export const formatCurrencyCompact = (value?: number | null): string => {
  if (value === null || value === undefined) return '-';

  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      notation: 'compact',
      compactDisplay: 'short',
    }).format(value);
  } catch {
    return '-';
  }
};

/**
 * Parses a Brazilian currency string back to a number
 * @param currencyString - Currency string in Brazilian format
 * @returns Parsed number or null if invalid
 * @example
 * parseCurrency('R$ 1.234,56') // Returns 1234.56
 * parseCurrency('1.234,56') // Returns 1234.56
 * parseCurrency('invalid') // Returns null
 */
export const parseCurrency = (currencyString?: string | null): number | null => {
  if (!currencyString) return null;

  try {
    // Remove currency symbol and spaces
    const cleanString = currencyString
      .replace(/R\$\s?/g, '')
      .replace(/\./g, '') // Remove thousands separator
      .replace(',', '.'); // Replace decimal separator

    const parsed = parseFloat(cleanString);
    return isNaN(parsed) ? null : parsed;
  } catch {
    return null;
  }
};

/**
 * Formats a percentage value
 * @param value - Numeric value to format (0-100 scale)
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted percentage string or '-' if invalid
 * @example
 * formatPercentage(12.345) // Returns '12,35%'
 * formatPercentage(12.345, 1) // Returns '12,3%'
 */
export const formatPercentage = (
  value?: number | null,
  decimals: number = 2
): string => {
  if (value === null || value === undefined) return '-';

  try {
    return new Intl.NumberFormat('pt-BR', {
      style: 'percent',
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(value / 100);
  } catch {
    return '-';
  }
};
